import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { configCollection, STORAGE_KEYS } from '../firebase/schema';
import { useCacheStore } from './cacheStore';
import { useNotificationStore } from './notificationStore';

export type PriceCalculationMethod = 'lowest' | 'highest' | 'average';
export type DisplayMode = 'single' | 'multiple';

interface CategoryConfig {
  displayMode: DisplayMode;
  priceCalculationMethod: PriceCalculationMethod;
  hasGraduations: boolean;
  hasNotes: boolean;
  message?: string;
  allowSimilarProducts?: boolean;
  onlyUnitPrice?: boolean;
  allowCalculator?: boolean;
}

interface GeneralConfig {
  logoUrl: string;
}

interface ConfigState {
  categories: Record<string, CategoryConfig>;
  general: GeneralConfig;
}

const DEFAULT_CATEGORY_CONFIG: CategoryConfig = {
  displayMode: 'multiple',
  priceCalculationMethod: 'lowest',
  hasGraduations: false,
  hasNotes: false,
  message: '',
  allowSimilarProducts: false,
  onlyUnitPrice: false,
  allowCalculator: true
};

const DEFAULT_GENERAL_CONFIG: GeneralConfig = {
  logoUrl: 'https://opticacroma.com.ar/wp-content/uploads/2024/05/croma-logo-1-e1715382202765.png'
};

export const useConfigStore = defineStore('config', () => {
  // Estado
  const categoryConfigs = ref<Record<string, CategoryConfig>>({});
  const generalConfig = ref<GeneralConfig>(DEFAULT_GENERAL_CONFIG);
  const loading = ref(false);
  const error = ref<string | null>(null);
  
  const cacheStore = useCacheStore();
  const notificationStore = useNotificationStore();

  // Inicializar configuración para una categoría específica
  const initializeCategoryConfig = (categoryId: string) => {
    if (!categoryConfigs.value[categoryId]) {
      categoryConfigs.value[categoryId] = { ...DEFAULT_CATEGORY_CONFIG };
    }
  };

  // Cargar configuraciones desde Firebase
  const fetchConfigurations = async () => {
    loading.value = true;
    error.value = null;

    try {
      // Verificar caché
      const isCacheFresh = await cacheStore.checkDataFreshness();
      const cachedConfigs = cacheStore.loadFromCache<ConfigState>(STORAGE_KEYS.CONFIGURATIONS);

      if (isCacheFresh && cachedConfigs) {
        categoryConfigs.value = cachedConfigs.categories;
        generalConfig.value = cachedConfigs.general || DEFAULT_GENERAL_CONFIG;
        return;
      }

      // Cargar desde Firebase
      const snapshot = await getDocs(configCollection);
      const configs: Record<string, CategoryConfig> = {};
      let general = DEFAULT_GENERAL_CONFIG;
      
      snapshot.forEach(doc => {
        if (doc.id === 'general') {
          general = doc.data() as GeneralConfig;
        } else {
          configs[doc.id] = doc.data() as CategoryConfig;
        }
      });

      categoryConfigs.value = configs;
      generalConfig.value = general;
      
      // Guardar en caché
      cacheStore.saveToCache(STORAGE_KEYS.CONFIGURATIONS, { 
        categories: configs,
        general
      });

    } catch (err) {
      console.error('Error loading configurations:', err);
      error.value = 'Error al cargar configuraciones';
      
      // Usar caché como fallback
      const cachedConfigs = cacheStore.loadFromCache<ConfigState>(STORAGE_KEYS.CONFIGURATIONS);
      if (cachedConfigs) {
        categoryConfigs.value = cachedConfigs.categories;
        generalConfig.value = cachedConfigs.general || DEFAULT_GENERAL_CONFIG;
      }
    } finally {
      loading.value = false;
    }
  };

  // Guardar configuraciones en Firebase
  const saveConfigurations = async () => {
    loading.value = true;
    error.value = null;

    try {
      const batch = writeBatch(db);

      // Guardar configuración general
      const generalRef = doc(configCollection, 'general');
      batch.set(generalRef, generalConfig.value);

      // Guardar cada configuración de categoría
      Object.entries(categoryConfigs.value).forEach(([categoryId, config]) => {
        const configRef = doc(configCollection, categoryId);
        batch.set(configRef, config);
      });

      await batch.commit();
      
      // Actualizar caché
      cacheStore.saveToCache(STORAGE_KEYS.CONFIGURATIONS, { 
        categories: categoryConfigs.value,
        general: generalConfig.value
      });

      notificationStore.success('Configuraciones guardadas correctamente');
    } catch (err) {
      console.error('Error saving configurations:', err);
      error.value = 'Error al guardar configuraciones';
      notificationStore.error('Error al guardar las configuraciones');
    } finally {
      loading.value = false;
    }
  };

  // Obtener configuración para una categoría específica
  const getCategoryConfig = (categoryId: string): CategoryConfig => {
    if (!categoryConfigs.value[categoryId]) {
      initializeCategoryConfig(categoryId);
    }
    return categoryConfigs.value[categoryId];
  };

  // Actualizar configuración para una categoría específica
  const updateCategoryConfig = (
    categoryId: string, 
    config: Partial<CategoryConfig>
  ) => {
    categoryConfigs.value[categoryId] = {
      ...getCategoryConfig(categoryId),
      ...config
    };
  };

  // Inicializar configuración
  fetchConfigurations();

  return {
    categoryConfigs,
    generalConfig,
    loading,
    error,
    getCategoryConfig,
    updateCategoryConfig,
    initializeCategoryConfig,
    saveConfigurations,
    fetchConfigurations
  };
});